import { HEX, NIGHTMARES, type NightmareType } from '@somnhold/content';
import { cellAtWorld, cellCenter, geo, heroWalkable } from '../grid.ts';
import { dist2, imax, imin, isqrt, mulBp, withinRange } from '../math/fixed.ts';
import type { BattleState, GustRules, GustState, Hero, SendaNpc } from '../types.ts';
import { ev, spawnEnemy } from './combat.ts';
import { stepToward } from './enemies.ts';
import { orderMove } from './heroes.ts';

/**
 * The windbreak (docs/sendas/guerrero.md, chapter 2, act 2: «El cortaviento»). On the rock platform in the middle of
 * the gorge Kel crosses with the stretcher, and the wind comes along the gorge in gusts that change side, seen `warn`
 * before in the snow lifting. A gust pushes whatever it catches in the open `push` hexes downwind and, catching Kel
 * full, knocks the stretcher out of his hands: gathering it is `drop`. Behind a planted cover the wind doesn't strike:
 * its lee runs `lee` hexes downwind and down its two slants (three rows of the way). Ari and Gerd, kneeling on one
 * flank of the middle, already cover it from one side; there are five covers more (two of the stretcher's shields and
 * three planks), planted standing still a moment and pulled again in passing. The flag shows the next two gusts'
 * sides: build a corridor of lee, knowing the next comes from the other side. Crows in flocks fly against the wind (it
 * never pushes them); the bone standard-bearer waits at the far end. Won with Kel across; lost with him or the hero down.
 *
 * Cost: a gust looks at the hero, Kel and the nightmares once; the lee is a handful of cells, worked out when asked.
 */

const fresh = (r: GustRules): GustState => ({
  cells: [],
  kinds: [],
  planks: r.covers - r.shields,
  shields: r.shields,
  next: 0,
  job: -1,
  held: 0,
  gather: 0,
  struck: 0,
  kept: 0,
  banner: false,
});

const kelOf = (s: BattleState): SendaNpc | undefined => s.senda?.npcs.find((n) => n.id === 'kel');

/** The neighbour directions downwind (along the row) and down its two slants (DIRS order), for a gust's side. */
const DOWN: Record<number, [number, number, number]> = { 1: [0, 1, 5], [-1]: [3, 2, 4] };

/**
 * The cells a gust blowing toward `dir` doesn't strike: behind each cover (Ari and Gerd's, and the planted ones),
 * `lee` along the row and one more down each slant (sorted, no repeats).
 */
export const gustLee = (s: BattleState, dir: number): number[] => {
  const r = s.rules?.senda?.gust;
  const w = s.senda?.gust;
  if (!r) return [];
  const nb = geo(s.map.radius).nb;
  const [along, up, down] = DOWN[dir > 0 ? 1 : -1] as [number, number, number];
  const out = new Set<number>();
  const run = (from: number, n: number): void => {
    let c = from;
    for (let k = 0; k < n && c >= 0; k++) {
      c = nb[c * 6 + along] as number;
      if (c >= 0) out.add(c);
    }
  };
  for (const c of [...r.fixed, ...(w?.cells ?? [])]) {
    run(c, r.lee);
    for (const d of [up, down]) {
      const s1 = nb[c * 6 + d] as number;
      if (s1 < 0) continue;
      out.add(s1);
      run(s1, r.lee - 1);
    }
  }
  return [...out].sort((a, b) => a - b);
};

/** The next gusts (the flag): the plan's next ones from the fight's clock, `n` of them. */
export const gustsAhead = (s: BattleState, n: number): GustRules['plan'] => {
  const r = s.rules?.senda?.gust;
  const w = s.senda?.gust;
  if (!r || !w) return [];
  return r.plan.slice(w.next, w.next + n);
};

/** Where a cover can go: open ground, not the way's two ends, not Ari's or Gerd's. */
const plantable = (s: BattleState, r: GustRules, cell: number): boolean => heroWalkable(s, cell) && !r.fixed.includes(cell);

/** The hero sent to a cell (a tap on it): to plant a cover there, or pull the one there. */
export const gustOrder = (s: BattleState, h: Hero, cell: number): void => {
  const r = s.rules?.senda?.gust;
  const w = s.senda?.gust;
  if (!r || !w || cell < 0) return;
  const there = w.cells.includes(cell);
  if (!there && (!plantable(s, r, cell) || w.planks + w.shields <= 0)) return;
  w.job = cell;
  w.held = 0;
  const c = cellCenter(s.map.radius, cell);
  orderMove(s, h, c.x, c.y);
  h.target = 0;
  h.forced = false;
};

const plant = (s: BattleState, w: GustState, cell: number): void => {
  const shield = w.planks <= 0;
  if (shield) w.shields--;
  else w.planks--;
  // Kept sorted, each cell's kind beside it.
  let i = 0;
  while (i < w.cells.length && (w.cells[i] as number) < cell) i++;
  w.cells.splice(i, 0, cell);
  w.kinds.splice(i, 0, shield ? 1 : 0);
  const c = cellCenter(s.map.radius, cell);
  ev(s, { t: 'senda', kind: 'plant', npc: '', x: c.x, y: c.y, n: shield ? 1 : 0 });
};

const pull = (s: BattleState, w: GustState, cell: number): void => {
  const i = w.cells.indexOf(cell);
  if (i < 0) return;
  if (w.kinds[i] === 1) w.shields++;
  else w.planks++;
  w.cells.splice(i, 1);
  w.kinds.splice(i, 1);
  const c = cellCenter(s.map.radius, cell);
  ev(s, { t: 'senda', kind: 'pull', npc: '', x: c.x, y: c.y });
};

/** The hero's hands: standing still by the cell it was sent to, the cover goes in (or out). */
const heroWork = (s: BattleState, r: GustRules, w: GustState, cue: (c: string) => void): void => {
  const h = s.heroes.find((x) => x.downTicks === 0);
  if (!h || w.job < 0) return;
  const c = cellCenter(s.map.radius, w.job);
  if (h.moving || !withinRange(h.x, h.y, c.x, c.y, r.reach)) {
    w.held = 0;
    // Stopped short of it (a gust carried it off, or the way was cut): the order is off.
    if (!h.moving) w.job = -1;
    return;
  }
  const there = w.cells.includes(w.job);
  if (!there && w.planks + w.shields <= 0) {
    w.job = -1;
    return;
  }
  if (++w.held < (there ? r.pull : r.stand)) return;
  const cell = w.job;
  w.job = -1;
  w.held = 0;
  if (there) pull(s, w, cell);
  else {
    const first = w.cells.length === 0 && w.planks + w.shields === r.covers;
    plant(s, w, cell);
    if (first) cue('plant');
  }
};

/** Pushed downwind up to `n` hexes, a hex at a time while the ground holds (never over the edge). */
const shove = (s: BattleState, o: { x: number; y: number }, dir: number, n: number): boolean => {
  let moved = false;
  for (let k = 0; k < n; k++) {
    const nx = o.x + dir * HEX;
    if (!heroWalkable(s, cellAtWorld(s, nx, o.y))) break;
    o.x = nx;
    moved = true;
  }
  return moved;
};

const strike = (s: BattleState, r: GustRules, w: GustState, dir: number, cue: (c: string) => void): void => {
  const lee = gustLee(s, dir);
  const open = (x: number, y: number): boolean => !lee.includes(cellAtWorld(s, x, y));
  for (const h of s.heroes) {
    if (h.downTicks > 0 || !open(h.x, h.y)) continue;
    if (!shove(s, h, dir, r.push)) continue;
    h.ax = h.x;
    h.ay = h.y;
    // Still walking somewhere: on from where the wind left it.
    if (h.moving) orderMove(s, h, h.destX, h.destY);
    else {
      h.destX = h.x;
      h.destY = h.y;
      h.path = [];
    }
  }
  for (const e of s.enemies) {
    if (e.hp <= 0 || NIGHTMARES[e.kind as NightmareType].flying || !open(e.x, e.y)) continue;
    shove(s, e, dir, r.push);
  }
  const kel = kelOf(s);
  if (kel && kel.hp > 0) {
    if (open(kel.x, kel.y)) {
      shove(s, kel, dir, r.push);
      w.gather = r.drop;
      w.struck++;
      // Down with the stretcher on the rock: a fall counts as a blow (docs/DECISIONS.md: without it, staying by Kel
      // won the crossing without a cover planted).
      kel.hp -= r.fall;
      ev(s, { t: 'senda', kind: 'hit', npc: kel.id, x: kel.x, y: kel.y });
      if (kel.hp <= 0) {
        kel.hp = 0;
        ev(s, { t: 'senda', kind: 'down', npc: kel.id, x: kel.x, y: kel.y });
      }
      ev(s, { t: 'senda', kind: 'knock', npc: 'kel', x: kel.x, y: kel.y, n: dir });
      if (w.struck === 1) cue('knock');
    } else w.kept++;
  }
};

export const gustTick = (s: BattleState, cue: (c: string) => void): void => {
  const r = s.rules?.senda?.gust;
  const st = s.senda;
  if (!r || !st) return;
  const w = (st.gust ??= fresh(r));
  if (!s.started) return;
  heroWork(s, r, w, cue);
  // The gusts: seen `warn` before, then striking.
  const g = r.plan[w.next];
  if (g) {
    if (s.waveT === g.t - r.warn) {
      const kel = kelOf(s);
      ev(s, { t: 'senda', kind: 'gustWarn', npc: '', x: kel?.x ?? 0, y: kel?.y ?? 0, n: g.dir });
    }
    if (s.waveT >= g.t) {
      strike(s, r, w, g.dir, cue);
      ev(s, { t: 'senda', kind: 'gust', npc: '', x: 0, y: 0, n: g.dir });
      w.next++;
    }
  }
  // Kel: gathering the stretcher, or walking on (a blow doesn't stop him here: he keeps his head down).
  const kel = kelOf(s);
  if (kel && kel.hp > 0) {
    kel.flinch = 0;
    kel.moving = false;
    if (w.gather > 0) {
      if (--w.gather === 0) ev(s, { t: 'senda', kind: 'gather', npc: 'kel', x: kel.x, y: kel.y });
    } else if (!withinRange(kel.x, kel.y, r.to.x, r.to.y, r.done)) {
      kel.moving = true;
      stepToward(kel, r.to.x, r.to.y, r.kel);
    }
    // The standard-bearer comes out of the far end as Kel gets this far.
    if (!w.banner && gustWay(s) * 10000 >= r.bannerBp * gustLen(r)) {
      w.banner = true;
      const b = spawnEnemy(s, 'bannerman', 1, false, 0, r.to.x, r.to.y - 900);
      if (s.rules?.enemyHpBp) b.hp = b.maxHp = imax(1, mulBp(b.maxHp, s.rules.enemyHpBp));
      cue('banner');
    }
  }
};

const gustLen = (r: GustRules): number => {
  const dx = r.to.x - r.from.x;
  const dy = r.to.y - r.from.y;
  return isqrt(dx * dx + dy * dy);
};

/** How far Kel has come along his way (fixed units). */
export const gustWay = (s: BattleState): number => {
  const r = s.rules?.senda?.gust;
  const kel = kelOf(s);
  if (!r || !kel) return 0;
  const total = gustLen(r);
  const left = isqrt(dist2(kel.x, kel.y, r.to.x, r.to.y));
  return imin(total, total - left > 0 ? total - left : 0);
};

export const gustGoal = (s: BattleState): { done: number; need: number } => {
  const r = s.rules?.senda?.gust;
  if (!r) return { done: 0, need: 1 };
  return { done: gustWay(s), need: gustLen(r) - r.done };
};

/** Won with Kel at the far end, the stretcher in his hands. */
export const gustResult = (s: BattleState): 'won' | null => {
  const r = s.rules?.senda?.gust;
  const w = s.senda?.gust;
  const kel = kelOf(s);
  return r && w && kel && kel.hp > 0 && w.gather === 0 && withinRange(kel.x, kel.y, r.to.x, r.to.y, r.done) ? 'won' : null;
};
